import {
  BUSINESS_VERTICALS,
  getBusinessVertical,
  type BusinessModuleId,
  type BusinessVerticalId,
  type BusinessVerticalManifest,
} from "@/lib/business-verticals";
import { businessPlatform } from "@/lib/restaurant-platform";

/**
 * Tenant capability / package flags (Phase 7).
 *
 * Implements the proposed pricing structure as config-driven feature gating —
 * NOT Stripe billing. A pilot tenant is configured here with only the packages
 * they are paying for or testing, and the admin navigation + route guards hide
 * unpurchased/unready modules.
 *
 * Packages (per the product brief):
 *   - core         ($500/mo): Website + vertical-specific conversion + email marketing
 *   - guestManager (+$200/mo): Owned customer/client database
 *   - aiVoice      (+$100/mo): AI voice add-on (gated; only safe feature-flagging)
 *
 * SMS and Meta/Google audience export are NOT purchasable packages — they stay
 * internal-only / disabled-by-default until their compliance foundations land
 * (see docs/PILOT_READINESS_SECURITY.md remaining risks).
 *
 * Source of truth: this map. Super_admin always bypasses gating so internal
 * operators can access every tool regardless of a tenant's package.
 */

export type PackageId = "core" | "guestManager" | "aiVoice";

export type TenantCapabilities = {
  /** Core is on for every configured tenant (it is the base package). */
  core: boolean;
  guestManager: boolean;
  aiVoice: boolean;
};

export type TenantBusinessProfile = {
  verticalId: BusinessVerticalId;
  capabilities: TenantCapabilities;
};

export type TenantId = string | undefined;
export type TenantContext =
  | TenantId
  | {
      restaurantId?: string;
      tenantId?: string;
      businessId?: string;
      verticalId?: BusinessVerticalId | string;
    }
  | null;

export const PACKAGE_LABELS: Record<PackageId, string> = {
  core: "Website + Leads + Email",
  guestManager: "Customer Directory",
  aiVoice: "AI Voice",
};

const VERTICAL_PACKAGE_LABELS: Partial<
  Record<BusinessVerticalId, Partial<Record<PackageId, string>>>
> = {
  restaurant: {
    core: "Website + Ordering + Email",
    guestManager: "Guest Manager",
  },
  barber_shop: {
    core: "Website + Booking + Email",
    guestManager: "Client Directory",
  },
};

export const PACKAGE_PRICES: Record<PackageId, number> = {
  core: 500,
  guestManager: 200,
  aiVoice: 100,
};

/**
 * Per-tenant capability configuration. Keyed by the legacy restaurantId field
 * used in convex/lib/platform.ts PLATFORM_ADMIN_LOCATIONS. Unknown tenants
 * get CORE ONLY — fail-closed toward the minimum safe package so a new tenant
 * can't accidentally see Guest Manager or AI Voice before being onboarded.
 */
export const TENANT_CAPABILITIES: Record<string, TenantCapabilities> = {
  // Tuscan Cove — full pilot stack (the reference tenant).
  "tuscan-cove": {
    core: true,
    guestManager: true,
    aiVoice: true,
  },
  // Roma Kitchen — pilot without AI Voice.
  "roma-kitchen": {
    core: true,
    guestManager: true,
    aiVoice: false,
  },
};

const DEFAULT_CAPABILITIES: TenantCapabilities = {
  core: true,
  guestManager: false,
  aiVoice: false,
};

function isBusinessVerticalId(value: unknown): value is BusinessVerticalId {
  return typeof value === "string" && value in BUSINESS_VERTICALS;
}

export function getTenantIdFromContext(context: TenantContext): TenantId {
  if (!context) return undefined;
  if (typeof context === "string") return context;
  return context.tenantId ?? context.businessId ?? context.restaurantId;
}

export function getVerticalIdFromContext(
  context: TenantContext,
): BusinessVerticalId {
  if (
    context &&
    typeof context === "object" &&
    isBusinessVerticalId(context.verticalId)
  ) {
    return context.verticalId;
  }
  return getVerticalIdForTenant(getTenantIdFromContext(context));
}

/** Resolve capabilities for a tenant. Unknown/missing → core only (fail-closed). */
export function getCapabilitiesForTenant(
  tenantId: TenantId,
): TenantCapabilities {
  if (!tenantId) return DEFAULT_CAPABILITIES;
  return TENANT_CAPABILITIES[tenantId] ?? DEFAULT_CAPABILITIES;
}

export function getCapabilitiesForContext(
  context: TenantContext,
): TenantCapabilities {
  return getCapabilitiesForTenant(getTenantIdFromContext(context));
}

export function getVerticalIdForTenant(tenantId: TenantId): BusinessVerticalId {
  if (!tenantId) return "generic_local_business";
  return (
    businessPlatform.businesses.find((business) => business.id === tenantId)
      ?.verticalId ?? "generic_local_business"
  );
}

export function getBusinessProfileForTenant(
  tenantId: TenantId,
): TenantBusinessProfile {
  return {
    verticalId: getVerticalIdForTenant(tenantId),
    capabilities: getCapabilitiesForTenant(tenantId),
  };
}

export function getBusinessProfileForContext(
  context: TenantContext,
): TenantBusinessProfile {
  return {
    verticalId: getVerticalIdFromContext(context),
    capabilities: getCapabilitiesForContext(context),
  };
}

export function getBusinessVerticalForTenant(
  tenantId: TenantId,
): BusinessVerticalManifest {
  return getBusinessVertical(getVerticalIdForTenant(tenantId));
}

export function getBusinessVerticalForContext(
  context: TenantContext,
): BusinessVerticalManifest {
  return getBusinessVertical(getVerticalIdFromContext(context));
}

export function getPackageLabelsForVerticalId(
  verticalId: BusinessVerticalId | undefined,
): Record<PackageId, string> {
  const vertical = getBusinessVertical(verticalId);
  return {
    ...PACKAGE_LABELS,
    ...VERTICAL_PACKAGE_LABELS[vertical.id],
  };
}

export function getPackageLabelsForTenant(
  tenantId: TenantId,
): Record<PackageId, string> {
  return getPackageLabelsForVerticalId(getVerticalIdForTenant(tenantId));
}

/** @deprecated Use getCapabilitiesForTenant. Kept for legacy restaurantId call sites. */
export function getCapabilitiesForRestaurant(
  restaurantId: TenantId,
): TenantCapabilities {
  return getCapabilitiesForTenant(restaurantId);
}

/** @deprecated Use getVerticalIdForTenant. Kept for legacy restaurantId call sites. */
export function getVerticalIdForRestaurant(
  restaurantId: TenantId,
): BusinessVerticalId {
  return getVerticalIdForTenant(restaurantId);
}

/** @deprecated Use getBusinessProfileForTenant. Kept for legacy restaurantId call sites. */
export function getBusinessProfileForRestaurant(
  restaurantId: TenantId,
): TenantBusinessProfile {
  return getBusinessProfileForTenant(restaurantId);
}

/** @deprecated Use getBusinessVerticalForTenant. Kept for legacy restaurantId call sites. */
export function getBusinessVerticalForRestaurant(
  restaurantId: TenantId,
): BusinessVerticalManifest {
  return getBusinessVerticalForTenant(restaurantId);
}

/** Whether a specific package is enabled for a tenant. */
export function hasPackage(restaurantId: TenantId, pkg: PackageId): boolean {
  return getCapabilitiesForTenant(restaurantId)[pkg] === true;
}

/**
 * Admin nav items that require a package beyond core. Used by admin-navigation
 * to filter the sidebar. Anything not listed here is part of core (or is an
 * internal-only tool governed separately by pilot mode / role).
 *
 * - /admin/guests (Guest Manager) requires guestManager
 * - /admin/voice-ai + voice routes require aiVoice
 */
export const PACKAGE_GATED_NAV_PATHS: Record<string, PackageId> = {
  "/admin/guests": "guestManager",
  "/admin/customers": "guestManager",
  "/admin/voice-logs": "aiVoice",
  "/admin/reservations/voice-logs": "aiVoice",
  "/admin/voice-ai": "aiVoice",
};

export const PACKAGE_GATED_MODULES: Partial<
  Record<BusinessModuleId, PackageId>
> = {
  guestDirectory: "guestManager",
  aiVoice: "aiVoice",
};

export const ADMIN_PATH_MODULES: Record<string, BusinessModuleId> = {
  "/admin": "overview",
  "/admin/analytics": "analytics",
  "/admin/voice-ai": "aiVoice",
  "/admin/inquiries": "leads",
  "/admin/catering": "catering",
  "/admin/private-events": "privateEvents",
  "/admin/candidates": "leads",
  "/admin/marketing": "marketing",
  "/admin/campaigns": "marketing",
  "/admin/email-automations": "marketing",
  "/admin/gbp": "gbpAgent",
  // Core package, not an add-on: deliberately absent from PACKAGE_GATED_MODULES.
  "/admin/social": "social",
  "/admin/guests": "guestDirectory",
  "/admin/customers": "guestDirectory",
  // /admin/inbox redirects to /admin/support (canonical Inbox). Gate on support
  // so tenants without guestDirectory can still open tickets, while
  // Guest CRM stays package-gated separately.
  "/admin/inbox": "support",
  "/admin/support": "support",
  "/admin/businesses": "support",
  "/admin/restaurants": "support",
  "/admin/messages": "support",
  "/admin/staff": "staff",
  "/admin/settings": "settings",
  "/admin/settings/announcements": "announcements",
  "/admin/settings/ordering": "orderingSettings",
  "/admin/settings/delivery-zones": "deliveryZones",
  "/admin/checklists": "checklists",
  "/admin/reservations": "reservations",
  "/admin/reservations/voice-logs": "aiVoice",
  "/admin/menu": "menu",
  "/admin/orders": "orders",
  "/admin/redemptions": "redemptions",
  "/admin/promos": "marketing",
  "/admin/events": "events",
  "/admin/sms": "support",
  // Disabled on every vertical. Mapping it here lets path guards deny
  // /admin/tips* instead of silently passing an unmapped prefix.
  "/admin/tips": "tips",
};

function uniqueModules(modules: BusinessModuleId[]): BusinessModuleId[] {
  return [...new Set(modules)];
}

export function getEnabledModulesForTenant(
  tenantId: TenantId,
): BusinessModuleId[] {
  return getEnabledModulesForContext(tenantId);
}

export function getEnabledModulesForContext(
  context: TenantContext,
): BusinessModuleId[] {
  const manifest = getBusinessVerticalForContext(context);
  const capabilities = getCapabilitiesForContext(context);

  return uniqueModules(manifest.defaultModules).filter((moduleId) => {
    const requiredPackage = PACKAGE_GATED_MODULES[moduleId];
    if (!requiredPackage) return true;
    return capabilities[requiredPackage] === true;
  });
}

/** @deprecated Use getEnabledModulesForTenant. Kept for legacy restaurantId call sites. */
export function getEnabledModulesForRestaurant(
  restaurantId: TenantId,
): BusinessModuleId[] {
  return getEnabledModulesForTenant(restaurantId);
}

export function hasTenantBusinessModule(
  tenantId: TenantContext,
  moduleId: BusinessModuleId,
): boolean {
  return getEnabledModulesForContext(tenantId).includes(moduleId);
}

export function hasTenantVerticalModule(
  tenantId: TenantContext,
  moduleId: BusinessModuleId,
): boolean {
  return getBusinessVerticalForContext(tenantId).defaultModules.includes(
    moduleId,
  );
}

/** @deprecated Use hasTenantBusinessModule. Kept for legacy restaurantId call sites. */
export function hasBusinessModule(
  restaurantId: TenantContext,
  moduleId: BusinessModuleId,
): boolean {
  return hasTenantBusinessModule(restaurantId, moduleId);
}

/** @deprecated Use hasTenantVerticalModule. Kept for legacy restaurantId call sites. */
export function hasVerticalModule(
  restaurantId: TenantContext,
  moduleId: BusinessModuleId,
): boolean {
  return hasTenantVerticalModule(restaurantId, moduleId);
}

export function getAdminPathModule(pathname: string): BusinessModuleId | null {
  const path = pathname.split("?")[0]?.split("#")[0] || pathname;

  if (path === "/admin" || path === "/admin/") return "overview";

  const candidates = Object.entries(ADMIN_PATH_MODULES).sort(
    ([left], [right]) => right.length - left.length,
  );
  const matched = candidates.find(([prefix]) => {
    if (prefix === "/admin") return path === "/admin" || path === "/admin/";
    return path === prefix || path.startsWith(`${prefix}/`);
  });

  return matched?.[1] ?? null;
}

/** True when the given admin href is gated by a package the tenant lacks. */
export function isPathHiddenByPackage(
  href: string,
  restaurantId: TenantContext,
): boolean {
  const pkg = PACKAGE_GATED_NAV_PATHS[href];
  if (!pkg) return false;
  return !getCapabilitiesForContext(restaurantId)[pkg];
}

/** True when an admin path belongs to a module this tenant's vertical/package lacks. */
export function isPathHiddenByBusinessProfile(
  href: string,
  restaurantId: TenantContext,
): boolean {
  const moduleId = getAdminPathModule(href);
  if (!moduleId) return false;
  return !hasBusinessModule(restaurantId, moduleId);
}

/** True when a path belongs to a module outside the tenant's business vertical. */
export function isPathOutsideBusinessVertical(
  href: string,
  restaurantId: TenantContext,
): boolean {
  const moduleId = getAdminPathModule(href);
  if (!moduleId) return false;
  return !hasVerticalModule(restaurantId, moduleId);
}

export { BUSINESS_VERTICALS };
